/**
 * #2740 (KATHY, /rewarehousing/location-assignment "clicking retrieve opens a new blank
 * window"): stale-tab chunk recovery.
 *
 * Root cause chain (reproduced live 2026-08-13 against ph10-test): a tab loaded BEFORE a
 * deploy holds the old hashed chunk URLs. The deploy find-wipes /assets and restarts the BE
 * (JWTs die → the next API call 401s → the api client clears the session → RequireAuth
 * navigates to /login). /login is React.lazy — its OLD chunk hash now 404s, the dynamic
 * import throws during render, and with no error boundary React unmounts the entire tree:
 * a silent BLANK page. Any lazy route the stale tab hadn't visited yet fails the same way.
 *
 * Vite (^5) dispatches a cancelable {@code vite:preloadError} window event when a dynamic
 * import's chunk (or one of its preloaded deps) fails to fetch. Recovery: swallow the error
 * and hard-reload — index.html is served no-cache, so the reload picks up the new hashes and
 * lands wherever the router was headed (e.g. the fresh /login). A sessionStorage timestamp
 * guards against a reload loop when chunks are missing for real (mid-deploy window, broken
 * deploy): within {@link RELOAD_LOOP_WINDOW_MS} of the previous auto-reload the event is left
 * alone, the error propagates, and {@code AppErrorBoundary} shows its visible fallback
 * instead of a blank page.
 */

const STORAGE_KEY = 'ph10-chunk-reload-at';
export const RELOAD_LOOP_WINDOW_MS = 30_000;

/**
 * Crash lane 19 Oct 2026 (RUSS /browse/carton-repack-ship-sio 17:06Z, ROGER
 * /browse/report-printers 17:30Z, LINK1 /browse/trucks 19:08Z): every one of those "crashes" was
 * this guard WORKING — nginx shows the stale tab 404 the old chunks, then fetch the new index
 * and the new page chunk in the same second. But the user saw the red "Something went wrong"
 * panel flash and the server logged a CLIENT ERROR, because:
 *  - preventDefault makes Vite's preload helper RESOLVE the import with `undefined`
 *    (`baseModule().catch(handlePreloadError)`), so React.lazy's initializer reads
 *    `undefined.default` and throws "Cannot read properties of undefined (reading 'default')"
 *    into the root boundary while the reload is still in flight (RUSS, LINK1);
 *  - a route needs several chunks, so several preloadError events fire in the SAME document; the
 *    second one saw the timestamp the first had just written, took it for a reload loop, and let
 *    "Failed to fetch dynamically imported module" throw (ROGER).
 * Fix: remember, per document, that a reload is already on its way. Further events in that
 * document are swallowed too (no second reload), and AppErrorBoundary asks
 * {@link isChunkReloadPending} so it shows a calm "loading the new version" note and tags the
 * report `stale-chunk-reload` instead of raising a crash. The 30 s loop guard still works across
 * documents: after the reload, a fresh document that fails again is NOT pending and sees the
 * recent timestamp, so the error reaches the boundary's Reload button as before.
 */
// Keyed by the Location object (one per document, so a reload starts a fresh slate) → the time
// the reload was issued. It expires after the loop window: if the reload never happened (the
// user answered "Stay on page" to an unsaved-changes prompt) the old behaviour comes back.
const reloadIssuedAt = new WeakMap<object, number>();

function markReloadPending(win: { location: object }): void {
  reloadIssuedAt.set(win.location, Date.now());
}

/** True while this document has a stale-chunk reload in flight (issued < 30 s ago). */
export function isChunkReloadPending(win: { location: object } = window): boolean {
  const at = reloadIssuedAt.get(win.location);
  return at !== undefined && Date.now() - at < RELOAD_LOOP_WINDOW_MS;
}

/** Minimal surface of `window` the guard touches — injectable for tests. */
export type ChunkReloadWindow = {
  addEventListener: (type: string, listener: (e: Event) => void) => void;
  sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
  location: { reload: () => void };
};

export function installChunkReloadGuard(win: ChunkReloadWindow = window): void {
  win.addEventListener('vite:preloadError', (e: Event) => {
    if (isChunkReloadPending(win)) {
      // A sibling chunk of the same navigation already triggered the reload — not a loop.
      e.preventDefault();
      return;
    }
    let last = 0;
    try {
      last = Number(win.sessionStorage.getItem(STORAGE_KEY) ?? '0') || 0;
    } catch {
      // sessionStorage unavailable (rare embedded contexts) — fall through and reload once
      // per document lifetime at most; the boundary still backstops any loop.
    }
    const now = Date.now();
    if (now - last < RELOAD_LOOP_WINDOW_MS) {
      // Second failure in quick succession — reloading didn't fix it. Let the error throw
      // so AppErrorBoundary renders a visible fallback instead of looping.
      return;
    }
    try {
      win.sessionStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      /* see above */
    }
    e.preventDefault(); // Vite: default-prevented ⇒ the import error is NOT thrown
    markReloadPending(win);
    win.location.reload();
  });
}

/**
 * slice-3709 (LINK1 on ph10-test, 2026-09-18 15:11 ET): after six deploys in a day her open tab
 * requested nine chunks that no longer existed (404) and the root boundary showed "Something went
 * wrong" on Test Screens, Testers Hub, v2 Screens and What's Happening — "no screens were given".
 * The vite:preloadError guard above never saw those: a lazy route whose chunk 404s (or whose
 * re-hashed shared chunk resolves without the expected export — "reading 'default'") surfaces as
 * a render error, not a preload event. This is the boundary-side twin: recognise the stale-chunk
 * signatures and reload ONCE per window (same session key + loop guard), else let the boundary
 * show its message with the Reload button.
 */
export const STALE_CHUNK_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading (CSS )?chunk [^ ]+ failed/i,
  /Unable to preload CSS/i,
  /Cannot read properties of undefined \(reading 'default'\)/,
];

export function isStaleChunkError(message: string | undefined | null): boolean {
  if (!message) return false;
  return STALE_CHUNK_PATTERNS.some((re) => re.test(message));
}

/** Reload once for a stale-chunk error; returns true when a reload was issued. */
export function reloadOnceForStaleChunk(
  error: { message?: string } | null | undefined,
  win: Pick<ChunkReloadWindow, 'sessionStorage' | 'location'> = window,
): boolean {
  if (!isStaleChunkError(error?.message)) return false;
  if (isChunkReloadPending(win)) return false; // already reloading — nothing more to do
  let last = 0;
  try {
    last = Number(win.sessionStorage.getItem(STORAGE_KEY) ?? '0') || 0;
  } catch {
    /* storage may be blocked — fall through and reload anyway, once */
  }
  const now = Date.now();
  if (now - last < RELOAD_LOOP_WINDOW_MS) return false;
  try {
    win.sessionStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    /* see above */
  }
  markReloadPending(win);
  win.location.reload();
  return true;
}

